const initialState = {
  dashboard: null,
  loading: false,
  error: null,
  hasUnsavedChanges: false,
  isSaving: false,
  isPublishing: false,
  // Undo/Redo state
  history: [], // מערך של 10 פעולות אחרונות
  historyIndex: -1, // מיקום נוכחי בהיסטוריה (-1 = אין היסטוריה עדיין)
  maxHistorySize: 10 // מקסימום פעולות
};

const dashboardReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'INITIALIZE_DASHBOARD_START':
      return { ...state, loading: true, error: null };

    case 'INITIALIZE_DASHBOARD_SUCCESS':
      const dashboard = action.payload.dashboard;

      // וודא שאנחנו משתמשים ב-current.widgets ולא בשדה הישן widgets
      const processedDashboard = {
        ...dashboard,
        // הסר את השדה הישן widgets כדי למנוע בלבול
        widgets: undefined,
        // וודא שיש current section - אל תשתמש בשדה הישן widgets כ-fallback
        current: dashboard.current || {
          widgets: [],
          settings: dashboard.settings || { bgColor: '#fcfbfa', bgImage: '' }
        }
      };

      // אתחול ההיסטוריה עם המצב הראשוני
      const initialHistoryState = {
        widgets: processedDashboard.current.widgets,
        settings: processedDashboard.current.settings
      };

      return {
        ...state,
        loading: false,
        dashboard: processedDashboard,
        error: null,
        isSaving: false,
        hasUnsavedChanges: false,
        // התחל עם המצב הראשוני בהיסטוריה
        history: [initialHistoryState],
        historyIndex: 0
      };

    case 'INITIALIZE_DASHBOARD_FAILURE':
      return { ...state, loading: false, error: action.payload };

    case 'UPDATE_DASHBOARD_START':
      return { ...state, isSaving: true, error: null };

    case 'UPDATE_DASHBOARD_SUCCESS':
      console.log('🔄 UPDATE_DASHBOARD_SUCCESS - Received payload:', {
        hasPayload: !!action.payload,
        payloadKeys: action.payload ? Object.keys(action.payload) : [],
        hasCurrent: !!action.payload?.current,
        hasWidgets: !!action.payload?.current?.widgets,
        widgetsCount: action.payload?.current?.widgets?.length || 0,
        widgets: action.payload?.current?.widgets?.map(w => ({ widgetId: w.widgetId, componentKey: w.componentKey }))
      });

      // אם קיבלנו dashboard מעודכן מ-Firestore, השתמש בו
      // אחרת, שמור על ה-dashboard הקיים
      const updatedDashboard = action.payload ? {
        ...action.payload,
        // וודא שאין שדה widgets ישן
        widgets: undefined,
        // וודא שיש current section
        current: action.payload.current || {
          widgets: [],
          settings: action.payload.settings || { bgColor: '#fcfbfa', bgImage: '' }
        }
      } : state.dashboard;

      console.log('✅ Updated dashboard in state:', {
        hasWidgets: !!updatedDashboard?.current?.widgets,
        widgetsCount: updatedDashboard?.current?.widgets?.length || 0
      });

      return {
        ...state,
        dashboard: updatedDashboard,
        hasUnsavedChanges: false,
        isSaving: false,
        // איפוס ההיסטוריה אחרי שמירה
        history: [],
        historyIndex: -1
      };

    case 'UPDATE_DASHBOARD_FAILURE':
      return { ...state, isSaving: false, error: action.payload };

    case 'UPDATE_DASHBOARD_LOCAL':
      // המצב החדש שאנחנו רוצים לעדכן אליו
      const newState = {
        widgets: [...action.payload.widgets],
        settings: state.dashboard?.current?.settings || { bgColor: '#fcfbfa', bgImage: '' }
      };

      // אם יש historyIndex שלא בסוף, מחק את כל מה שאחריו (כי עשינו שינוי חדש)
      const newHistory = state.historyIndex < state.history.length - 1
        ? state.history.slice(0, state.historyIndex + 1)
        : [...state.history];

      // הוסף את המצב החדש להיסטוריה
      newHistory.push(newState);

      // שמור רק את ה-10 האחרונים
      const trimmedHistory = newHistory.length > state.maxHistorySize
        ? newHistory.slice(newHistory.length - state.maxHistorySize)
        : newHistory;

      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          current: {
            ...state.dashboard.current,
            widgets: newState.widgets,
            settings: newState.settings
          }
        },
        hasUnsavedChanges: true,
        history: trimmedHistory,
        historyIndex: trimmedHistory.length - 1
      };

    case 'UPDATE_DASHBOARD_SETTINGS':
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          current: {
            ...state.dashboard.current,
            settings: action.payload.settings
          }
        },
        hasUnsavedChanges: true
      };

    case 'PUBLISH_VERSION_START':
      return { ...state, isPublishing: true, error: null };

    case 'PUBLISH_VERSION_SUCCESS':
      return {
        ...state,
        hasUnsavedChanges: false,
        isPublishing: false
      };

    case 'FETCH_DASHBOARD_SUCCESS':
      return {
        ...state,
        dashboard: {
          ...action.payload,
          // וודא שאין שדה widgets ישן
          widgets: undefined,
          // וודא שיש current section
          current: action.payload.current || {
            widgets: [],
            settings: action.payload.settings || { bgColor: '#fcfbfa', bgImage: '' }
          }
        },
        loading: false,
        error: null
      };

    case 'PUBLISH_VERSION_FAILURE':
      return { ...state, isPublishing: false, error: action.payload };

    case 'LOAD_VERSION_SUCCESS':
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          current: {
            widgets: [...action.payload.version.widgets],
            settings: action.payload.version.settings
          }
        },
        hasUnsavedChanges: false
      };

    case 'LOAD_VERSION_TO_DRAFT':
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          current: {
            widgets: [...action.payload.widgets],
            settings: action.payload.settings
          }
        },
        hasUnsavedChanges: true // ← חשוב! זה מסמן שיש שינויים שלא נשמרו
      };

    case 'UNDO':
      // צריך לפחות 2 פריטים בהיסטוריה כדי לחזור אחורה
      if (state.historyIndex <= 0 || state.history.length < 2) {
        return state; // אין מה לבטל
      }

      const previousIndex = state.historyIndex - 1;
      const previousState = state.history[previousIndex];

      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          current: {
            widgets: [...previousState.widgets],
            settings: previousState.settings
          }
        },
        historyIndex: previousIndex,
        hasUnsavedChanges: true
      };

    case 'REDO':
      if (state.historyIndex >= state.history.length - 1) {
        return state; // אין מה לשחזר
      }

      const nextIndex = state.historyIndex + 1;
      const nextState = state.history[nextIndex];

      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          current: {
            widgets: [...nextState.widgets],
            settings: nextState.settings
          }
        },
        historyIndex: nextIndex,
        hasUnsavedChanges: true
      };

    case 'CLEAR_HISTORY':
      return {
        ...state,
        history: [],
        historyIndex: -1
      };

    case 'LOGOUT_START':
      return initialState;

    default:
      return state;
  }
};

export default dashboardReducer;